import React from 'react';
import { useEffect, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useParams, useLocation } from 'react-router';
import { sortBy, difference } from 'lodash';
import { AppStoreState } from 'types';
import { fetchLessonPlan } from 'state/modules/entity/thunks/lesson-planner';
import {
  sectionTypes,
  getDefaultStepOneFormState,
  getBlankLessonData
} from 'state/providers/LessonAssignmentProvider/util';
import { WithDragDrop } from 'components/@layout/WithDragDrop';
import Panels from './StepOnePanels';
import { getWeekdays } from 'util/lesson-plan-util';
import { parseQueryParams } from 'util/query-params-util';
import { addDays, getDayOfWeek, formatFullDate, isEqualDate, isSameDay } from 'util/date-util';
import { AssignmentEngineParams } from 'state/providers/LessonAssignmentProvider/types';
import { fetchUserClassrooms } from 'state/modules/entity/custom/classrooms/thunks';
import { getUserAssignmentsView } from 'state/modules/entity/thunks/assignment';
import { translate } from 'translations/translator';
import useLessonAssignment from 'state/providers/LessonAssignmentProvider';

import AssignmentVariant from 'translations/variants/lessonAssignment';
const translateAssignment = key => translate('lessonAssignment', key);

const LessonAssignmentStepOne = () => {
  const dispatch = useDispatch() as any;
  const { productCurriculumId, epochTime }: any = useParams();
  const location = useLocation();
  const lessons = useSelector(({ entity }: AppStoreState) => entity.lessonPlan?.lessons);
  const userAssignments: any = useSelector(
    ({ entity }: AppStoreState) => entity.userAssignments,
    shallowEqual
  );
  const classrooms = useSelector(({ entity }: AppStoreState) => entity.classrooms);
  const locale = useSelector(({ app }: AppStoreState) => app.locale);
  const queryParam = parseQueryParams(location.search);
  const [{ stepOneFormState }, { stepOneFormDispatch, lessonAssignmentDispatch }] =
    useLessonAssignment();

  const getParamDate = useCallback(() => new Date(Number.parseInt(epochTime, 10)), [epochTime]);

  const getActiveLesson = useCallback(
    lessons => {
      const date: any = getParamDate();
      return lessons.find(l => isEqualDate(new Date(l.lessonDate || l.date) as any, date));
    },
    [getParamDate]
  );

  const getLessonPlanData = async () => await dispatch(fetchLessonPlan(productCurriculumId));

  const getPrepopulatedData = useCallback(
    defaultData =>
      Object.keys(stepOneFormState.form).length > 0 ? stepOneFormState.form : defaultData,
    [stepOneFormState.form]
  );

  const mapLessons = useCallback(
    currentWeek =>
      currentWeek.reduce((acc, day) => {
        if (!day.lessons) return acc;
        const result = day.lessons.map(lesson => {
          const missing = difference(
            sectionTypes,
            lesson.sections.map(({ type }) => type)
          );
          missing.forEach(type =>
            lesson.sections.push({
              title: translateAssignment(AssignmentVariant[type]),
              type,
              activities: []
            })
          );
          const sortedSections = sortBy(lesson.sections, ({ type }) => sectionTypes.indexOf(type));

          return {
            ...getDefaultStepOneFormState(),
            id:
              queryParam?.assignment === AssignmentEngineParams.EDIT
                ? String(lesson.id)
                : String(lesson.teachingOrderLessonId),
            lesson: { ...lesson, sections: sortedSections },
            dayOfWeek: day.dayOfWeek,
            startDate: lesson?.dateAssigned
              ? formatFullDate(new Date(lesson.dateAssigned))
              : formatFullDate(new Date(day.lessonDate)),
            assigned: day.assigned || false,
            title: lesson.title,
            instructions: lesson?.instructions ?? '',
            dueDate: lesson?.dateDue
              ? formatFullDate(new Date(lesson.dateDue))
              : formatFullDate(addDays(new Date(day.lessonDate), 2)),
            audioFiles: lesson?.files ?? []
          };
        });

        return [...acc, ...result];
      }, []),
    [queryParam]
  );

  const getWeekLessons = useCallback(
    lessons => {
      const date: any = getParamDate();
      const activeLesson = { ...getActiveLesson(lessons), assigned: true };
      const targetIndex = lessons.findIndex(l =>
        isEqualDate(new Date(l.lessonDate || l.date) as any, date)
      );
      const relativeWeekIndex = getWeekdays(locale).indexOf(activeLesson.dayOfWeek);

      if (relativeWeekIndex === 0) {
        return [activeLesson, ...lessons.slice(targetIndex + 1, targetIndex + 5)];
      }

      if (relativeWeekIndex === 4) {
        return [activeLesson];
      }

      const calcRemainder = nextPos =>
        getWeekdays(locale).length - (relativeWeekIndex + 1) + nextPos;
      const remainingRemainingLessons = lessons.slice(
        targetIndex + 1,
        targetIndex + calcRemainder(1)
      );

      return [activeLesson, ...remainingRemainingLessons];
    },
    [locale, getParamDate, getActiveLesson]
  );

  const handleBlankAssignInit = useCallback(() => {
    const date = getParamDate();
    const blankData: any = [
      {
        id: AssignmentEngineParams.BLANK,
        dayOfWeek: getDayOfWeek(date),
        startDate: formatFullDate(date),
        dueDate: formatFullDate(addDays(date, 2)),
        assigned: true,
        ...getBlankLessonData(),
        ...getDefaultStepOneFormState()
      }
    ];
    const prepopulateBlankData = getPrepopulatedData(blankData);

    stepOneFormDispatch.setForm(prepopulateBlankData, { keyBy: 'id' });
    lessonAssignmentDispatch.setActiveForm(AssignmentEngineParams.BLANK);
    return; // eslint-disable-next-line
  }, [lessonAssignmentDispatch, getParamDate, stepOneFormDispatch]);

  const handleAssignmentInit = useCallback(() => {
    const weekLessons = mapLessons(getWeekLessons(lessons));
    const activeLesson = getActiveLesson(lessons);
    const selectedDay = weekLessons.find(({ startDate }) =>
      isSameDay(startDate, activeLesson.lessonDate)
    );

    const prepopulateData = getPrepopulatedData(weekLessons);

    stepOneFormDispatch.setForm(prepopulateData, { keyBy: 'id' });
    lessonAssignmentDispatch.setActiveForm(String(selectedDay.id));
  }, [
    getActiveLesson,
    getWeekLessons,
    lessonAssignmentDispatch,
    lessons,
    stepOneFormDispatch,
    mapLessons,
    getPrepopulatedData
  ]);

  const handleEditAssignmentInit = useCallback(() => {
    const activeLesson = getActiveLesson(userAssignments);

    const lesson = activeLesson.assignments.filter(
      ({ id }) => id === Number(queryParam?.assignmentId) ?? ''
    );

    const editAssignmentData = mapLessons([
      {
        ...activeLesson,
        lessons: lesson,
        dayOfWeek: activeLesson.dayOfWeek,
        lessonDate: activeLesson.date,
        assigned: true
      }
    ]);

    const prepopulateData = getPrepopulatedData(editAssignmentData);

    stepOneFormDispatch.setForm(prepopulateData, { keyBy: 'id' });
    lessonAssignmentDispatch.setActiveForm(editAssignmentData[0].id);
  }, [
    userAssignments,
    getPrepopulatedData,
    lessonAssignmentDispatch,
    stepOneFormDispatch,
    mapLessons,
    getActiveLesson,
    queryParam
  ]);

  const handleSingleAssignInit = useCallback(() => {
    const weekLessons = mapLessons(getWeekLessons(lessons));
    const selectedDay = weekLessons.find(({ id }) => id === queryParam?.id);

    const singleAssignData = mapLessons([
      {
        ...selectedDay,
        lessons: [selectedDay.lesson],
        dayOfWeek: selectedDay.dayOfWeek,
        lessonDate: selectedDay.startDate,
        assigned: true
      }
    ]);

    const prepopulateData = getPrepopulatedData(singleAssignData);

    stepOneFormDispatch.setForm(prepopulateData, { keyBy: 'id' });
    lessonAssignmentDispatch.setActiveForm(singleAssignData[0].id);
  }, [
    getPrepopulatedData,
    lessonAssignmentDispatch,
    stepOneFormDispatch,
    mapLessons,
    queryParam,
    lessons,
    getWeekLessons
  ]);

  const initStates = useCallback(async () => {
    await lessonAssignmentDispatch.setClassrooms(classrooms);

    switch (queryParam?.assignment) {
      case AssignmentEngineParams.BLANK:
        handleBlankAssignInit();
        break;
      case AssignmentEngineParams.EDIT:
        if (userAssignments?.length) {
          await handleEditAssignmentInit();
          break;
        }
        break;
      case AssignmentEngineParams.SINGLE:
        handleSingleAssignInit();
        break;
      default:
        handleAssignmentInit();
        return;
    }
  }, [
    userAssignments,
    handleBlankAssignInit,
    handleAssignmentInit,
    handleEditAssignmentInit,
    handleSingleAssignInit,
    queryParam,
    lessonAssignmentDispatch,
    classrooms
  ]);

  useEffect(() => {
    if (!lessons) getLessonPlanData();
    if (!classrooms.length) dispatch(fetchUserClassrooms());
    if (!userAssignments?.length) dispatch(getUserAssignmentsView());

    if (lessons) initStates();
    // eslint-disable-next-line
  }, [lessons, dispatch, productCurriculumId, userAssignments]);

  const form = stepOneFormState.form;
  const assigned = useCallback(
    () => (Object.values(form) as any).filter(x => x.assigned === true),
    [form]
  );
  const unassigned = useCallback(
    () => (Object.values(form) as any).filter(x => x.assigned === false),
    [form]
  );

  return (
    <>
      <WithDragDrop>
        <Panels data={assigned()} isAssigned={true} />
        <Panels data={unassigned()} isAssigned={false} />
      </WithDragDrop>
    </>
  );
};

export default LessonAssignmentStepOne;
